import { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Link, useLocation } from 'react-router-dom';
import { Menu, ChevronDown, Settings, ArrowRight, Search, X } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '../components/ui/sheet';
import { useAuth } from '../context/AuthContext';
import { useNavigation } from '../context/NavigationContext';
import { MEGA_RENDERERS, MegaSearch } from './nav/MegaMenus';
import api from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Navbar({ configOverride, previewMode = false }) {
  const { t, lang, setLang } = useLanguage();
  const { config: liveConfig } = useNavigation();
  const config = configOverride || liveConfig;

  const [megaOpen, setMegaOpen] = useState(false);
  const [hoveredCat, setHoveredCat] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileMegaOpen, setMobileMegaOpen] = useState(false);
  const [mobileFamily, setMobileFamily] = useState('');
  const [tagline, setTagline] = useState('');

  useEffect(() => {
    api.get('/settings/public/site_settings')
      .then(r => setTagline(r.data?.tagline || ''))
      .catch(() => {});
  }, []);
  const [overlayMenu, setOverlayMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [cats, setCats] = useState([]);
  const location = useLocation();
  const { isAdmin } = useAuth();
  const timeoutRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/catalog/megamenu?lang=${lang}`)
      .then(r => r.json())
      .then(d => { if (Array.isArray(d) && d.length) setCats(d); })
      .catch(() => {});
  }, [lang]);

  useEffect(() => {
    if (previewMode) return;
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [previewMode]);

  useEffect(() => { setMegaOpen(false); setMobileOpen(false); setOverlayMenu(false); }, [location]);

  if (!config) return null;

  const template = config.nav_template || 'inline';
  const MegaBody = MEGA_RENDERERS[config.mega_template || 'rail'] || MEGA_RENDERERS.rail;
  const items = (config.header || []).filter(i => i.visible !== false);
  const label = (i) => (lang === 'ar' && i.label_ar ? i.label_ar : i.label);

  /* apply the admin's featured-grade override on top of the live catalogue */
  const resolvedCats = cats.map(c => {
    const pick = (config.mega_featured || {})[c.slug];
    const match = pick && c.items.find(it => it.slug === pick);
    return match ? { ...c, featured: { ...c.featured, name: match.name, slug: match.slug, img: match.img || c.featured.img } } : c;
  });
  const cat = resolvedCats[hoveredCat] || resolvedCats[0];

  const megaEnter = () => { clearTimeout(timeoutRef.current); setMegaOpen(true); };
  const megaLeave = () => { timeoutRef.current = setTimeout(() => setMegaOpen(false), 250); };

  const isActive = (i) => i.url === '/' ? location.pathname === '/' : location.pathname.startsWith(i.url);
  const overlay = !previewMode && location.pathname === '/' && !scrolled && !megaOpen && template !== 'pill';

  const linkCls = (active) => `px-data relative px-3 py-2 text-[11px] font-medium uppercase tracking-[0.16em] whitespace-nowrap transition-colors duration-400 ${
    overlay ? (active ? 'text-white' : 'text-white/65 hover:text-white')
            : (active ? 'text-[var(--px-ink)]' : 'text-[var(--px-ink-soft)] hover:text-[var(--px-ink)]')}`;

  const iconBtn = `grid place-items-center rounded border transition-colors duration-400 ${
    overlay ? 'border-white/25 text-white/70 hover:text-white hover:border-white'
            : 'px-hair text-[var(--px-ink-soft)] hover:text-[var(--px-ink)] hover:border-[var(--px-ink)]'}`;

  const Indicator = ({ active }) => (
    <span className={`absolute left-3 right-3 -bottom-[1px] h-[2px] origin-left transition-transform duration-500 ${
      overlay ? 'bg-white' : 'bg-[var(--px-ink)]'} ${active ? 'scale-x-100' : 'scale-x-0'}`} />
  );

  const Brand = ({ size = 'h-9 md:h-11' }) => (
    <Link to="/" data-testid="nav-logo" aria-label="Axicor DWC LLC" className="flex items-center gap-4 shrink-0">
      <img
        src={overlay ? '/brand/axicor-logo-white.png' : '/brand/axicor-logo.png'}
        alt="Axicor DWC LLC"
        className={`w-auto transition-all duration-500 ${scrolled ? 'h-8 md:h-9' : size}`}
      />
      {tagline && (
        <span
          data-testid="nav-tagline"
          className={`px-data hidden xl:block text-[9px] uppercase tracking-[0.22em] leading-tight max-w-[180px] ps-4 border-s ${
            overlay ? 'text-white/55 border-white/20' : 'text-[var(--px-ink-soft)] border-[var(--px-line)]'}`}
        >
          {tagline}
        </span>
      )}
    </Link>
  );

  const NavItem = ({ i }) => {
    if (i.type === 'mega') {
      return (
        <div className="static" onMouseEnter={megaEnter} onMouseLeave={megaLeave}>
          <Link to={i.url} data-testid="nav-products" className={`flex items-center gap-1.5 ${linkCls(isActive(i))}`}>
            {label(i)}
            <ChevronDown className={`w-3 h-3 transition-transform duration-400 ${megaOpen ? 'rotate-180' : ''}`} />
            <Indicator active={isActive(i)} />
          </Link>
        </div>
      );
    }
    if (i.children?.length) {
      return (
        <div className="relative group">
          <Link to={i.url} data-testid={`nav-${i.id}`} className={`flex items-center gap-1.5 ${linkCls(isActive(i))}`}>
            {label(i)} <ChevronDown className="w-3 h-3" />
            <Indicator active={isActive(i)} />
          </Link>
          <div className="absolute top-full start-0 min-w-[220px] bg-white border px-hair shadow-[0_30px_60px_-30px_rgba(11,25,36,0.3)] opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300">
            {i.children.filter(c => c.visible !== false).map(c => (
              <Link
                key={c.id}
                to={c.url}
                target={c.new_tab ? '_blank' : undefined}
                rel={c.new_tab ? 'noopener noreferrer' : undefined}
                data-testid={`nav-child-${c.id}`}
                className="px-data block px-5 py-3 text-[10px] uppercase tracking-[0.16em] text-[var(--px-ink-soft)] hover:text-[var(--px-ink)] hover:bg-[var(--px-paper)] border-b px-hair last:border-0 transition-colors"
              >
                {label(c)}
              </Link>
            ))}
          </div>
        </div>
      );
    }
    return (
      <Link
        to={i.url}
        target={i.new_tab ? '_blank' : undefined}
        rel={i.new_tab ? 'noopener noreferrer' : undefined}
        data-testid={`nav-${i.id}`}
        className={linkCls(isActive(i))}
      >
        {label(i)}
        {i.badge && <span className="px-data ms-1.5 px-pill bg-[var(--px-accent)] text-white text-[8px] px-1.5 py-[1px] align-middle">{i.badge}</span>}
        <Indicator active={isActive(i)} />
      </Link>
    );
  };

  const Utilities = () => (
    <div className="flex items-center">
      <button data-testid="nav-lang-toggle" onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')} className={`px-data w-11 h-9 text-[10px] tracking-[0.12em] ${iconBtn}`}>
        {lang === 'ar' ? 'EN' : 'AR'}
      </button>
      <button data-testid="nav-search-trigger" aria-label="Search the site" onClick={() => window.dispatchEvent(new Event('axicor:open-search'))} className={`ms-2 w-9 h-9 ${iconBtn}`}>
        <Search className="w-3.5 h-3.5" />
      </button>
      <Link to="/contact" data-testid="nav-rfq-button" className={`px-data group ms-3 inline-flex items-center gap-2 rounded px-5 py-3 text-[10px] font-medium uppercase tracking-[0.18em] transition-colors duration-500 ${
        overlay ? 'bg-white text-[var(--px-ink)] hover:bg-[var(--px-accent)] hover:text-white' : 'bg-[var(--px-ink)] text-white hover:bg-[var(--px-accent)]'}`}>
        {t('nav.requestQuote')}
        <ArrowRight className="w-3.5 h-3.5 transition-transform duration-500 group-hover:translate-x-1 rtl-flip" />
      </Link>
      {isAdmin && (
        <Link to="/admin" data-testid="nav-admin-link" className={`ms-2 w-9 h-9 ${iconBtn}`}><Settings className="w-3.5 h-3.5" /></Link>
      )}
    </div>
  );

  const Mega = () => megaOpen && cat && (
    <div
      data-testid="products-mega-menu"
      className="absolute top-full left-0 right-0 bg-[var(--px-surface)] border-x border-b px-hair shadow-[0_40px_80px_-40px_rgba(11,25,36,0.35)] overflow-x-hidden max-h-[calc(100vh-7rem)] overflow-y-auto overscroll-contain"
      onMouseEnter={megaEnter}
      onMouseLeave={megaLeave}
    >
      <MegaSearch cats={resolvedCats} t={t} onNavigate={() => setMegaOpen(false)}>
        <MegaBody cats={resolvedCats} cat={cat} setCat={setHoveredCat} hovered={hoveredCat} links={config.mega_links || []} lang={lang} t={t} />
      </MegaSearch>
    </div>
  );

  const MobileDrawer = () => (
    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
      <SheetTrigger asChild>
        <button data-testid="mobile-menu-toggle" className={`w-9 h-9 ${iconBtn}`}><Menu className="w-4 h-4" /></button>
      </SheetTrigger>
      <SheetContent side="left" className="bg-[var(--px-surface)] border-e px-hair w-[88vw] max-w-sm p-0 overflow-y-auto">
        <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
        <div className="px-6 py-6 border-b px-hair">
          <img src="/brand/axicor-logo.png" alt="Axicor DWC LLC" className="h-9 w-auto" />
        </div>
        <div className="flex flex-col">
          {items.map(i => i.type === 'mega' ? (
            <div key={i.id}>
              <button onClick={() => setMobileMegaOpen(!mobileMegaOpen)} data-testid="mobile-products-toggle" className="px-data w-full flex items-center justify-between px-6 py-4 border-b px-hair text-[11px] uppercase tracking-[0.18em] text-[var(--px-ink)]">
                {label(i)}
                <ChevronDown className={`w-4 h-4 transition-transform duration-400 ${mobileMegaOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileMegaOpen && (
                <div className="bg-[var(--px-paper)] border-b px-hair py-1">
                  <MegaSearch cats={resolvedCats} t={t} compact onNavigate={() => setMobileOpen(false)}>
                    <div>
                      {resolvedCats.map(g => (
                    <div key={g.slug} className="border-b px-hair last:border-0">
                      <button
                        onClick={() => setMobileFamily(mobileFamily === g.slug ? '' : g.slug)}
                        data-testid={`mobile-family-toggle-${g.slug}`}
                        className="w-full flex items-center justify-between gap-3 px-6 py-3 text-start"
                      >
                        <span className="px-display text-sm font-semibold text-[var(--px-ink)]">{g.title}</span>
                        <span className="flex items-center gap-2 shrink-0">
                          <span className="px-data text-[10px] tracking-[0.14em] text-[var(--px-ink-soft)]">{g.items.length}</span>
                          <ChevronDown className={`w-3.5 h-3.5 text-[var(--px-ink-soft)] transition-transform duration-300 ${mobileFamily === g.slug ? 'rotate-180' : ''}`} />
                        </span>
                      </button>
                      {mobileFamily === g.slug && (
                        <div className="pb-2.5">
                          {g.items.map(it => (
                            <Link
                              key={it.slug}
                              to={it.url || `/materials/${g.slug}/${it.slug}`}
                              onClick={() => setMobileOpen(false)}
                              className="grade-name block ps-10 pe-6 py-2 text-sm text-[var(--px-ink-soft)]"
                            >
                              {it.name}
                            </Link>
                          ))}
                          <Link
                            to={g.url || `/materials/${g.slug}`}
                            onClick={() => setMobileOpen(false)}
                            className="px-data block ps-10 pe-6 py-2 text-[10px] uppercase tracking-[0.18em] text-[var(--px-accent)]"
                          >
                            {t('nav.browseAll')} &rarr;
                          </Link>
                        </div>
                      )}
                    </div>
                  ))}
                    </div>
                  </MegaSearch>
                  {(config.mega_links || []).filter(l => l.visible !== false).map(l => (
                    <Link key={l.id} to={l.url} onClick={() => setMobileOpen(false)} className="px-data block px-6 py-2.5 text-[10px] uppercase tracking-[0.18em] text-[var(--px-ink)]">{label(l)} &rarr;</Link>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div key={i.id}>
              <Link to={i.url} onClick={() => setMobileOpen(false)} data-testid={`mobile-nav-${i.id}`} className="px-data block px-6 py-4 border-b px-hair text-[11px] uppercase tracking-[0.18em] text-[var(--px-ink)]">
                {label(i)}
              </Link>
              {(i.children || []).filter(c => c.visible !== false).map(c => (
                <Link key={c.id} to={c.url} onClick={() => setMobileOpen(false)} className="px-data block ps-10 pe-6 py-3 border-b px-hair text-[10px] uppercase tracking-[0.16em] text-[var(--px-ink-soft)]">
                  {label(c)}
                </Link>
              ))}
            </div>
          ))}
          <div className="p-6 flex flex-col gap-3">
            <Link to="/contact" className="px-data block text-center bg-[var(--px-ink)] text-white rounded px-6 py-4 text-[10px] uppercase tracking-[0.18em]">{t('nav.requestQuote')}</Link>
            <button data-testid="mobile-lang-toggle" onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')} className="px-data rounded border px-hair px-6 py-3 text-[10px] uppercase tracking-[0.18em] text-[var(--px-ink-soft)]">
              {lang === 'ar' ? 'English' : 'العربية'}
            </button>
            {isAdmin && <Link to="/admin" className="px-data text-center py-3 text-[10px] uppercase tracking-[0.18em] text-[var(--px-ink-soft)]">Admin</Link>}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );

  const shell = overlay
    ? 'bg-transparent border-b border-white/10'
    : scrolled
      ? 'bg-white/85 backdrop-blur-xl border-b px-hair shadow-[0_1px_30px_-12px_rgba(11,25,36,0.25)]'
      : 'bg-white border-b px-hair';

  /* ── template: overlay (minimal bar + full-screen menu) ── */
  if (template === 'overlay') {
    return (
      <nav data-testid="main-navbar" data-template="overlay" className={`${previewMode ? 'relative' : 'fixed'} top-0 left-0 right-0 z-50 transition-all duration-500 ${shell}`}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
          <Brand />
          <div className="flex items-center gap-2">
            <button data-testid="nav-lang-toggle" onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')} className={`px-data w-11 h-9 text-[10px] tracking-[0.12em] ${iconBtn}`}>{lang === 'ar' ? 'EN' : 'AR'}</button>
            <Link to="/contact" data-testid="nav-rfq-button" className={`px-data hidden sm:inline-flex items-center gap-2 rounded px-5 py-3 text-[10px] uppercase tracking-[0.18em] ${overlay ? 'bg-white text-[var(--px-ink)]' : 'bg-[var(--px-ink)] text-white'}`}>{t('nav.requestQuote')}</Link>
            <button data-testid="nav-overlay-toggle" onClick={() => setOverlayMenu(!overlayMenu)} className={`w-10 h-10 ${iconBtn}`}>
              {overlayMenu ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>
        {overlayMenu && (
          <div data-testid="nav-overlay-panel" className="fixed inset-0 top-20 bg-[var(--px-ink)] overflow-y-auto">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                {items.map((i, n) => (
                  <Link key={i.id} to={i.url} onClick={() => setOverlayMenu(false)} className="group flex items-baseline gap-5 border-b border-white/10 py-5">
                    <span className="px-data text-[10px] tracking-[0.2em] text-white/35">{String(n + 1).padStart(2, '0')}</span>
                    <span className="px-display text-2xl md:text-4xl font-semibold text-white group-hover:text-[var(--px-accent)] transition-colors">{label(i)}</span>
                  </Link>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-x-8 gap-y-6 content-start">
                {resolvedCats.map(c => (
                  <div key={c.slug}>
                    <Link to={`/materials/${c.slug}`} onClick={() => setOverlayMenu(false)} className="px-data block text-[9px] uppercase tracking-[0.25em] text-white/40 mb-2">{c.title}</Link>
                    {c.items.slice(0, 4).map(it => (
                      <Link key={it.slug} to={`/materials/${c.slug}/${it.slug}`} onClick={() => setOverlayMenu(false)} className="grade-name block text-sm text-white/70 hover:text-white py-0.5">{it.name}</Link>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </nav>
    );
  }

  /* ── template: stacked (utility strip + main row) ── */
  if (template === 'stacked') {
    return (
      <nav data-testid="main-navbar" data-template="stacked" className={`${previewMode ? 'relative' : 'fixed'} top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-white/90 backdrop-blur-xl border-b px-hair' : 'bg-white border-b px-hair'}`}>
        <div className="bg-[var(--px-ink)]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-9 flex items-center justify-between">
            <span className="px-data text-[10px] tracking-[0.18em] text-white/55">sales@axicor.ae &nbsp;·&nbsp; +971 4 357 9568</span>
            <button data-testid="nav-lang-toggle" onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')} className="px-data text-[10px] tracking-[0.18em] text-white/70 hover:text-white">{lang === 'ar' ? 'EN' : 'AR'}</button>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <div className="flex items-center justify-between h-[72px]">
            <Brand size="h-9 md:h-10" />
            <div className="hidden lg:flex items-center gap-0.5">{items.map(i => <NavItem key={i.id} i={i} />)}</div>
            <div className="hidden lg:flex items-center">
              <button data-testid="nav-search-trigger" aria-label="Search the site" onClick={() => window.dispatchEvent(new Event('axicor:open-search'))} className={`w-9 h-9 ${iconBtn}`}><Search className="w-3.5 h-3.5" /></button>
              <Link to="/contact" data-testid="nav-rfq-button" className="px-data ms-3 inline-flex items-center gap-2 bg-[var(--px-ink)] text-white rounded px-5 py-3 text-[10px] uppercase tracking-[0.18em] hover:bg-[var(--px-accent)] transition-colors">{t('nav.requestQuote')}</Link>
            </div>
            <div className="lg:hidden"><MobileDrawer /></div>
          </div>
          <Mega />
        </div>
      </nav>
    );
  }

  /* ── template: pill (floating glass bar) ── */
  if (template === 'pill') {
    return (
      <nav data-testid="main-navbar" data-template="pill" className={`${previewMode ? 'relative' : 'fixed'} top-0 left-0 right-0 z-50 px-4 md:px-8 pt-4`}>
        <div className="max-w-[1400px] mx-auto relative bg-white/85 backdrop-blur-xl border px-hair rounded-2xl shadow-[0_20px_60px_-30px_rgba(11,25,36,0.4)]">
          <div className="px-5 md:px-7 flex items-center justify-between h-[68px]">
            <Brand size="h-8 md:h-9" />
            <div className="hidden lg:flex items-center gap-0.5">{items.map(i => <NavItem key={i.id} i={i} />)}</div>
            <div className="hidden lg:flex"><Utilities /></div>
            <div className="lg:hidden"><MobileDrawer /></div>
          </div>
          {megaOpen && cat && (
            <div data-testid="products-mega-menu" className="absolute top-full left-0 right-0 mt-2 bg-[var(--px-surface)] border px-hair rounded-2xl overflow-x-hidden max-h-[calc(100vh-7rem)] overflow-y-auto overscroll-contain shadow-[0_40px_80px_-40px_rgba(11,25,36,0.35)]" onMouseEnter={megaEnter} onMouseLeave={megaLeave}>
              <MegaSearch cats={resolvedCats} t={t} onNavigate={() => setMegaOpen(false)}>
        <MegaBody cats={resolvedCats} cat={cat} setCat={setHoveredCat} hovered={hoveredCat} links={config.mega_links || []} lang={lang} t={t} />
      </MegaSearch>
            </div>
          )}
        </div>
      </nav>
    );
  }

  /* ── templates: inline (default), centered, split ── */
  const rowCls = template === 'centered'
    ? 'grid grid-cols-[auto_1fr_auto] items-center h-20 gap-6'
    : 'flex items-center justify-between h-20';

  return (
    <nav data-testid="main-navbar" data-template={template} className={`${previewMode ? 'relative' : 'fixed'} top-0 left-0 right-0 z-50 transition-all duration-500 ${shell}`}>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
        {template === 'split' ? (
          <div className="flex items-center justify-between h-20">
            <div className="hidden lg:flex items-center gap-0.5 flex-1">
              {items.slice(0, Math.ceil(items.length / 2)).map(i => <NavItem key={i.id} i={i} />)}
            </div>
            <div className="flex-1 lg:flex-none flex justify-start lg:justify-center"><Brand /></div>
            <div className="hidden lg:flex items-center gap-0.5 flex-1 justify-end">
              {items.slice(Math.ceil(items.length / 2)).map(i => <NavItem key={i.id} i={i} />)}
              <span className="mx-3 h-5 w-[1px] bg-[var(--px-line)]" />
              <Link to="/contact" data-testid="nav-rfq-button" className={`px-data inline-flex items-center gap-2 rounded px-5 py-3 text-[10px] uppercase tracking-[0.18em] transition-colors ${overlay ? 'bg-white text-[var(--px-ink)]' : 'bg-[var(--px-ink)] text-white hover:bg-[var(--px-accent)]'}`}>{t('nav.requestQuote')}</Link>
            </div>
            <div className="lg:hidden"><MobileDrawer /></div>
          </div>
        ) : (
          <div className={rowCls}>
            <Brand />
            <div className={`hidden lg:flex items-center gap-0.5 ${template === 'centered' ? 'justify-center' : ''}`}>
              {items.map(i => <NavItem key={i.id} i={i} />)}
              {template === 'inline' && <span className="mx-3 h-5 w-[1px] bg-[var(--px-line)]" />}
              {template === 'inline' && <Utilities />}
            </div>
            {template === 'centered' && <div className="hidden lg:flex justify-end"><Utilities /></div>}
            <div className="lg:hidden flex items-center gap-2">
              <button data-testid="nav-search-trigger-mobile" aria-label="Search the site" onClick={() => window.dispatchEvent(new Event('axicor:open-search'))} className={`w-9 h-9 ${iconBtn}`}><Search className="w-3.5 h-3.5" /></button>
              <MobileDrawer />
            </div>
          </div>
        )}
        <Mega />
      </div>
    </nav>
  );
}
